import {useTranslation} from "react-i18next";
import {Card, Col, Row} from "reactstrap";
import {API_URL} from "../../config";
import {CustomButton} from "../../Common/Components/Button/CustomButton";
import React, { useState } from "react";
import {ChangeDateFormat} from "../../Common/Helpers/ChangeDateFormat";
import {ToCustomDecimal} from "../../AppSystem/Helpers/ToCustomDecimal";

export function CustomReimbursementExpenseView({data, accountingResponse = null, editMode, onClickDelete, onClickEdit, participantsNode = null, attachmentsNode = null, commentNode = null, onClickRequired = null, additionalInfoList = [] }) {
    const { t } = useTranslation();

    const isReadOnlyProceeding = editMode === undefined && !onClickDelete && !onClickEdit;
    const [expandedItems, setExpandedItems] = useState({});

    const expenseSignature = Array.isArray(data)
        ? data.map(item => `${item?.id ?? ''}|${item?.accountingId ?? ''}|${item?.dateReceive ?? ''}|${item?.amount ?? 0}|${item?.fileId ?? ''}`).join('||')
        : '';

    React.useEffect(() => {
        setExpandedItems({});
    }, [expenseSignature]);

    if (isReadOnlyProceeding) {
        const list = Array.isArray(data) ? data : (data ? [data] : []);
        if (list.length === 0) return null;

        return (
            <>
                {list.map((item, index) => {
                    const accountingName = item?.accountingName || "";
                    const receiptName = item?.fileExtension || "";
                    const receiptHref = item?.fileId ? `${API_URL}/file?id=${item.fileId}` : null;

                    return (
                        <div key={`expense-proceeding-${item?.id ?? index}`} className="reimbursement-section-card">
                            <div className="reimbursement-section-title rokkitt-font">{t("reimbursement.expenseTitle")}</div>
                            <div className="reimbursement-fields">
                                <div className="reimbursement-field">
                                    <div className="reimbursement-field-label">{t("modelLabel.dateReceive")}</div>
                                    <div className="reimbursement-field-value">{ChangeDateFormat("date", item?.dateReceive)}</div>
                                </div>
                                <div className="reimbursement-field">
                                    <div className="reimbursement-field-label">{t("modelLabel.accountingId")}</div>
                                    <div className="reimbursement-field-value">{accountingName || "-"}</div>
                                </div>
                                <div className="reimbursement-divider" />
                                <div className="reimbursement-field">
                                    <div className="reimbursement-field-label">{t("reimbursement.amount")}</div>
                                    <div className="reimbursement-field-value">
                                        {ToCustomDecimal(item?.amount ?? 0)} {t("modelLabel.kr")}
                                    </div>
                                </div>
                                <div className="reimbursement-field">
                                    <div className="reimbursement-field-label">{t("modelLabel.vatAmount")}</div>
                                    <div className="reimbursement-field-value">
                                        {ToCustomDecimal(item?.vatAmount ?? 0)} {t("modelLabel.kr")}
                                    </div>
                                </div>

                                {receiptHref && receiptName && (
                                    <>
                                        <div className="reimbursement-divider" />
                                        <div className="reimbursement-file-items">
                                            <a href={receiptHref} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                                <div className="reimbursement-file-item">
                                                    <div className="reimbursement-file-left">
                                                        <div className="reimbursement-file-icon">
                                                            <i className="bx bx-file" />
                                                        </div>
                                                        <div className="reimbursement-file-name">{receiptName}</div>
                                                    </div>
                                                    <i className="bx bx-chevron-right reimbursement-file-arrow" />
                                                </div>
                                            </a>
                                        </div>
                                    </>
                                )}

                                {index === 0 && participantsNode}
                                <div className="reimbursement-divider" />
                                <div className="crv-total-row">
                                    <div className="reimbursement-field-label">{t("modelLabel.total")}</div>
                                    <div className="reimbursement-big-value">
                                        {ToCustomDecimal(item?.amount ?? 0)} {t("modelLabel.kr")}
                                    </div>
                                </div>
                                {index === 0 && attachmentsNode}
                                {index === 0 && commentNode}
                            </div>
                        </div>
                    );
                })}
            </>
        );
    }

    const toggleItem = (index) => setExpandedItems(prev => ({ ...prev, [index]: !prev[index] }));

    const HasRequiredDocumentationPending = (item) => {
        if (!item?.requiredDocumentation || item.requiredDocumentation.length === 0) {
            return false;
        }

        const relatedAdditionalInfo = Array.isArray(additionalInfoList)
            ? additionalInfoList.filter((infoItem) => infoItem.accountingId === item.accountingId)
            : [];

        const hasAdditionalDocumentation = relatedAdditionalInfo.some((infoItem) => {
            const info = infoItem?.additionalInfo || {};
            const hasProgram = !!info?.program;
            const hasParticipantsFile = !!info?.participantsFileId;
            const hasProgramFile = !!info?.programFileId;
            const hasAnyDynamicField = Object.values(info).some((value) => value !== "" && value !== null && value !== undefined);
            return hasProgram || hasParticipantsFile || hasProgramFile || hasAnyDynamicField;
        });

        return !hasAdditionalDocumentation;
    };

    return (
        <>
            {data && data.length > 0 ? data.map((item, index) => {
                const isExpanded = !!expandedItems[index];
                const canEdit = item?.status === 'Förslag' && editMode !== true;

                let accountingName = item?.accountingName || "";
                if (accountingResponse !== null && accountingName === "") {
                    accountingResponse.forEach((accounting) => {
                        if (accounting.id === item.accountingId) accountingName = accounting.showLabel;
                    });
                }

                const hasFile = item?.fileExtension && item?.fileExtension !== "" && item?.fileId;

                return (
                    <div key={`expense${index}`} className="crv-card-outer">
                        <div className="crv-header" onClick={() => toggleItem(index)}>
                            <div className="crv-header-left">
                                <i className="bx bx-receipt crv-header-icon" />
                                <span className="crv-header-label">
                                    {t('reimbursement.expenseTitle')} - {accountingName ? [...accountingName.matchAll(/\(([^()]*)\)/g)].pop()?.[1] || '' : ''}
                                </span>
                            </div>
                            <div className="crv-header-right">
                                {!isExpanded && (
                                    <span className="crv-header-amount">{ToCustomDecimal(item?.amount ?? 0)} kr</span>
                                )}
                                <i className={`bx ${isExpanded ? "bx-chevron-up" : "bx-chevron-down"} crv-chevron`} />
                            </div>
                        </div>

                        <div className="crv-collapse" style={{ maxHeight: isExpanded ? "500px" : "0px" }}>
                            <div className="crv-collapse-inner">
                                <div className={`crv-inner-card ${canEdit ? "crv-inner-card-with-buttons" : ""}`}>
                                    <div className="crv-row-mb">
                                        <span className="crv-label">{t('modelLabel.dateReceive')}:</span>
                                        <span className="crv-value">{ChangeDateFormat('date', item.dateReceive)}</span>
                                    </div>

                                    {hasFile && (
                                        <div className="crv-row-gap crv-row-mb">
                                            <span className="crv-label-shrink">{t('reimbursement.attachedFile') || "Bifogad fil"}:</span>
                                            <a
                                                href={`${API_URL}/file?id=${item.fileId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="crv-file-link"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                {item.fileName || item.fileExtension}
                                            </a>
                                        </div>
                                    )}

                                    <div className="crv-separator" />

                                    <div className="crv-row">
                                        <span className="crv-label-bold">{t('modelLabel.amount')}:</span>
                                        <span className="crv-value-bold">{ToCustomDecimal(item?.amount ?? 0)} kr</span>
                                    </div>

                                    {HasRequiredDocumentationPending(item) && (
                                        <div className="crv-required-doc" onClick={onClickRequired} style={{ cursor: onClickRequired ? 'pointer' : 'default' }}>
                                            <div className="crv-required-doc-left">
                                                <i className="bx bx-error-circle crv-required-doc-icon" />
                                                <span className="crv-required-doc-text">
                                                    {t('reimbursement.expenseRequiresAttachment') || "Utlägg kräver obligatorisk bilaga*"}
                                                </span>
                                            </div>
                                            <i className="bx bx-chevron-right crv-required-doc-arrow" />
                                        </div>
                                    )}
                                </div>

                                {canEdit && (
                                    <div className="crv-action-buttons">
                                        <button type="button" onClick={() => onClickDelete(item, index)} className="crv-btn-action">
                                            <i className="bx bx-trash-alt crv-icon-delete" />
                                        </button>
                                        <button type="button" onClick={() => onClickEdit(item, index)} className="crv-btn-action">
                                            <i className="bx bxs-pencil crv-icon-edit" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }) : ''}
        </>
    );
}